/**
 * Registration page component
 * Allows new users to create an account with username, email, and password
 *
 * US-002 + single-source-of-truth (2026-07-02): required affirmative consent gate.
 *   - tncChecked state (default false); Sign Up button stays disabled until checked.
 *   - ONE checkbox whose label links OUT to the canonical Terms of Service AND Privacy Policy
 *     on the marketing site (TERMS_URL / PRIVACY_URL) — new tab, no embedded copy. The old
 *     in-app /terms TermsPage (which embedded a divergent draft) was retired in this change.
 *   - tncAccepted in the register() payload carries the real tncChecked value (combined consent
 *     covering both documents). The client sends NO version — the backend stamps the two
 *     server-authoritative consent records (terms* + privacy*). See BUG-20260630-114135.
 *   - isSubmitDisabled extended with || !tncChecked (AC-1, AC-2).
 *   - Defensive guard in handleSubmit prevents submission if box is somehow unticked.
 *
 * TS-015 (T2-T5): Renamed the optional code field from "invite code" to "referral code":
 *   - `?ref=CODE` deep-link prefill (editable) via useSearchParams (TO-LL-013).
 *   - Debounced, NON-BLOCKING soft validation reusing TS-012's
 *     createDebouncedReferralValidator; an unrecognised code shows a gentle advisory
 *     notice but NEVER disables submit (BR-TS015.1, FR-2.5 — golden invariant: the
 *     referral code never blocks signup). isSubmitDisabled deliberately gains NO
 *     referral-related term.
 *   - Submit payload now sends `referralCode` (was `inviteCode`) — see types/auth.ts.
 *   - Removed `INVALID_INVITE_CODE` from the amber-banner conditional (backend no
 *     longer returns it) and removed the old demo-oriented helper copy (AC-TS015.5).
 *   - Enforces TO-LL-036's advisory-exception form: validateReferralCode/the debounced
 *     wrapper never throw, so a validate-unavailable failure surfaces the same gentle
 *     notice, never a block.
 *   - The referral state, both effects, the validator instance and the US-005 gate now
 *     live in useReferralField; the field and consent JSX live in ReferralCodeField /
 *     ConsentCheckbox. Behaviour is unchanged — see those files.
 *
 * Per-method signup cards (2026-07-16): the page renders TWO cards — a Google card
 * and an email card — instead of one form serving both doors.
 *   - WHY: a single referral field and a single T&C tick previously fed BOTH doors,
 *     with the tick sitting inside the email field stack while also gating the Google
 *     button below it. Parents could not tell which method the shared fields applied to.
 *   - CONSENT IS NOW PER-CARD (tncChecked / googleTncChecked). This is the real split:
 *     each tick gates only the button in its own card. Do NOT re-merge them — an
 *     affirmative consent must be bound to the door it authorises.
 *   - THE REFERRAL CODE IS DELIBERATELY STILL SHARED: ONE useReferralField() binding is
 *     rendered by both cards, so the value mirrors and no one retypes it after switching
 *     door. It also means the two fields can never disagree about a code's validity —
 *     which matters because validity gates BOTH doors (promo-code US-005). A code that
 *     settles invalid therefore blocks both cards' buttons. That is intended: there is
 *     one code, so there is one verdict.
 *
 * Always-clickable buttons (2026-08-04): BOTH buttons are now permanently clickable and every
 * gate moved from render time to CLICK time.
 *   - WHY: an incomplete form used to grey the button out and say nothing. A parent missing
 *     one of five conditions had a dead button and no explanation. Now the click always does
 *     something: it either submits or opens ValidationAlertDialog listing what is wrong, then
 *     focuses the first offending field on dismiss.
 *   - THE RULES DID NOT CHANGE, only WHEN they run. lib/registerValidation.ts holds the exact
 *     set that used to live in isSubmitDisabled and the Google button's `blocked` expression,
 *     in visual field order (the order drives which field gets focus).
 *   - CONSENT IS STILL A REAL GATE. Clicking an enabled button is NOT acceptance: handleSubmit
 *     refuses to call register() while any issue is present, and the Google button's
 *     onValidate returns false before it captures consent or redirects. US-002 / FR-G-001 /
 *     NFR-PDPA-001 / TO-LL-036 all still hold. Do not "simplify" this into submitting first.
 *   - IN-FLIGHT IS THE ONE REMAINING DISABLE, and it must stay. Without it a double tap fires
 *     two register calls, each landing a Cognito verification email against a pool capped at
 *     50 a day.
 *   - THE FORM CARRIES noValidate. The inputs keep their `required` attributes for assistive
 *     tech, but the browser's own validation UI is off: with a clickable button it would fire
 *     first and show its native bubble instead of the dialog. Do not remove noValidate without
 *     removing `required`, or the dialog becomes unreachable for empty fields.
 */

import { useState, useEffect, useRef } from 'react';
// BUG-20260713-020205 (D2, Option B) / T-10 (ISS-L04): added useLocation so the
// bounce message from OAuthCallbackPage (location.state?.message) can be rendered,
// mirroring LoginPage's existing successMessage banner pattern.
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { useAuth } from '@/hooks/useAuth';
import { validatePassword } from '@/lib/passwordValidator';
import { OTTER_INPUT_CLASS } from '@/lib/formStyles';
// BUG-20260701-155024: use centralized ROUTES constant (TO-LL-013) so the navigate
// target stays in sync with the route table — prevents stale-redirect regressions. - 2026-07-01
import { ROUTES } from '@/constants/routes';
import logoImage from '@/assets/thinkotter-logo.png';
import loginBg from '@/assets/login-bg.svg';
// google-sign-in/US-001 (T5, AC-001.1): "Continue with Google" entry point on the
// shared login/register page.
import { ContinueWithGoogleButton } from '@/components/auth/ContinueWithGoogleButton';
import { isOAuthConfigured } from '@/lib/amplifyConfig';
// promo-code US-005 (T1) / AC-005.7: per-door gate-rejection telemetry, never
// throws, never logs the raw code.
import { emitGateRejection } from '@/lib/gateTelemetry';
// GA page view. This page is the ONLY place in the app that loads gtag.js —
// see lib/analytics.ts for why that scope is deliberate and must not widen.
import { trackRegisterPageView } from '@/lib/analytics';
// Per-method signup cards (2026-07-16): referral state + the US-005 gate, and the two
// pieces of JSX each card renders. CODE_INVALID_MSG is re-exported through the hook so
// the reverse-flow CTA below can pass it as codeBlockedNotice.
import { useReferralField, CODE_INVALID_MSG } from '@/hooks/useReferralField';
import { ReferralCodeField } from '@/components/auth/ReferralCodeField';
import { ConsentCheckbox } from '@/components/auth/ConsentCheckbox';
// Always-clickable buttons (2026-08-04): the click-time rules and the pop-up that reports
// them. See the module headers for why the issue ORDER is load-bearing.
import {
  collectEmailSignupIssues,
  collectGoogleSignupIssues,
  type RegisterValidationIssue,
} from '@/lib/registerValidation';
import { ValidationAlertDialog } from '@/components/auth/ValidationAlertDialog';

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isLoading, error } = useAuth();
  // TS-015 / T2: read the `?ref=` deep-link query param (TO-LL-013 — useSearchParams,
  // BUG-20260713-020205 (D2, Option B) / T-10: bounce message from
  // OAuthCallbackPage when a not-yet-registered (consentPending) Google account
  // clicked "Continue with Google" on the LOGIN page. Mirrors LoginPage's
  // successMessage-from-location.state pattern exactly.
  const bounceMessage = location.state?.message;

  // Signup simplification (2026-07-29): username and displayName are GONE from this form.
  //   - username is generated server-side (`parent_<random>`) and returned in the 201. The
  //     parent never sees or types it; it exists only so verify can confirm without a lookup.
  //     Email cannot BE the username — Cognito rejects an email-format username on an
  //     email-alias pool (InvalidParameterException, probed against the live pool).
  //   - displayName defaults to "Parent" server-side. Google parents get their real name
  //     from the IdP mapping instead, so only email signups see the default.
  // Do not reintroduce either field without reading VerifyEmailPage first — the generated
  // username is the reason that page has an email fallback at all.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Always-clickable buttons (2026-08-04): the `localError` state that used to hold the inline
  // "Passwords do not match" style messages is GONE. Every client-side validation message now
  // goes to the pop-up, so the red text under the form is reserved exclusively for SERVER
  // errors from the register call. Two surfaces for the same class of message was the thing
  // worth removing; do not reintroduce a client-side path into that line.
  //
  // US-002 / AC-1 / AC-2: T&C consent gate. Default false so the parent must explicitly tick
  // the box. Enforces TO-LL-036 (no silent bypass).
  //
  // Per-method signup cards (2026-07-16): ONE consent flag PER DOOR. tncChecked gates
  // ONLY the native Sign Up button; googleTncChecked gates ONLY the Continue with
  // Google button. They are intentionally independent — a tick must authorise the door
  // it sits in. Do not collapse them back into one flag: that shared tick (which lived
  // in the email field stack yet also gated the Google button) is the exact confusion
  // this split removes.
  const [tncChecked, setTncChecked] = useState(false);
  const [googleTncChecked, setGoogleTncChecked] = useState(false);

  // TS-015 / TS-012 / BUG-20260702-205850 / promo-code US-004+US-005: the referral
  // field's value, `?ref=` prefill, debounced three-state validation, lock-after-valid
  // and the settled-invalid gate all live here now. Called ONCE and rendered by BOTH
  // cards — that shared binding is what syncs the two fields and guarantees they can
  // never disagree about a code's validity.
  const referral = useReferralField();
  // promo-code US-005 (T3) / AC-005.1: ONE code means ONE verdict, so a settled-invalid
  // code disables BOTH doors — exactly as it did when the page had a single form.
  const codeInvalid = referral.isInvalid;

  // promo-code US-005 (T3) / AC-005.7: tracks the PRIOR codeInvalid value so
  // telemetry is emitted only on the transition into disabled, not on every
  // render/re-evaluation.
  const wasCodeInvalidRef = useRef(false);

  // Always-clickable buttons (2026-08-04): the issues from the last blocked click. null means
  // the dialog is closed. Holding the full issue objects (not just their messages) is what
  // lets the dismiss handler focus issues[0].fieldId.
  const [validationIssues, setValidationIssues] = useState<RegisterValidationIssue[] | null>(null);

  const { isValid: isPasswordValid } = validatePassword(password);

  // GA: the ONLY analytics call site in the app. Measures how many people
  // reach this page but never sign up (the signup side of that subtraction
  // comes from the database). Mount-only, empty dep array. gtag.js is loaded
  // lazily inside this call, so it is never requested on any other route —
  // see the scope note in lib/analytics.ts before adding a second call site.
  useEffect(() => {
    trackRegisterPageView();
  }, []);

  // promo-code US-005 (T3) / AC-005.7: emit per-door gate-rejection telemetry
  // once on the transition into disabled (not on every render). Both doors are
  // reported because the shared code gates both.
  useEffect(() => {
    if (codeInvalid && !wasCodeInvalidRef.current) {
      const codeLength = referral.trimmedCode.length;
      emitGateRejection('native', codeLength);
      emitGateRejection('google', codeLength);
    }
    wasCodeInvalidRef.current = codeInvalid;
  }, [codeInvalid, referral.trimmedCode]);

  // Always-clickable buttons (2026-08-04): close the dialog, then move focus to the first
  // offending field.
  //
  // Focus is moved AFTER the dialog unmounts (hence the timeout) for two reasons: while it is
  // open its Tab handler traps focus on its own button, and the field may sit behind the
  // overlay. document.getElementById rather than a ref because the target varies per click
  // across six possible fields in two sibling cards, several of which are rendered by shared
  // child components (ReferralCodeField, ConsentCheckbox) that own their own markup. The ids
  // are the same ones those components already require for their Label htmlFor bindings.
  const dismissValidationDialog = () => {
    const focusFieldId = validationIssues?.[0]?.fieldId;
    setValidationIssues(null);
    if (!focusFieldId) return;
    setTimeout(() => {
      document.getElementById(focusFieldId)?.focus();
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Always-clickable buttons (2026-08-04): the checks that used to live here inline (and in
    // isSubmitDisabled) now come back as an ordered list, so the parent sees ALL of them at
    // once instead of fixing one, clicking, and discovering the next. The register() call
    // below is unreachable while any issue stands, which is what keeps the consent gate real
    // (US-002 / AC-1 / TO-LL-036) now that the button no longer carries it.
    //
    // The username and display-name checks were removed with their fields (2026-07-29) — both
    // values are now server-generated/defaulted.
    const issues = collectEmailSignupIssues({
      email,
      password,
      confirmPassword,
      isPasswordValid,
      codeInvalid,
      tncChecked,
    });
    if (issues.length > 0) {
      setValidationIssues(issues);
      return;
    }

    // Call register mutation.
    // tncAccepted is the single combined consent flag — one affirmative checkbox covering
    // BOTH the Terms of Service and the Privacy Policy. The backend records two independently-
    // versioned consent entries (terms* + privacy*) from this single acceptance, stamping the
    // server-authoritative versions itself; the client never asserts a version.
    // TS-015 / T5: referralCode (was inviteCode) is omitted when blank so the backend treats
    // blank-code registration as a clean parent signup. No validity check gates this call —
    // an unrecognised code still submits (BR-TS015.1, AC-TS015.3).
    register(
      {
        email,
        password,
        referralCode: referral.trimmedCode || undefined,
        tncAccepted: tncChecked,
      },
      {
        // `register` is registerMutation.mutate, so this per-call onSuccess receives the
        // mutation DATA — the hook's own onSuccess (useAuth.ts) is separate and both fire.
        onSuccess: (data) => {
          // BUG-20260701-155024: navigate directly to the canonical route so location.state
          // is not stripped by the '/verify' alias redirect. - 2026-07-01
          //
          // Signup simplification (2026-07-29): the username in this state is now the
          // SERVER-GENERATED one from the 201 — the parent never typed it and could never
          // retype it. That makes this the fast path, not the only path: VerifyEmailPage
          // falls back to asking for the email if this state is ever lost (refresh, new tab,
          // another device). `email` is carried so that fallback can prefill rather than ask.
          // codeResent tells the verify screen to say we resent an existing code rather than
          // created a new account.
          navigate(ROUTES.VERIFY_EMAIL, {
            state: {
              username: data.username,
              email,
              codeResent: data.codeResent,
            },
          });
        },
      }
    );
  };

  // Always-clickable buttons (2026-08-04): the ONLY remaining disable reason. Everything else
  // that used to be in this expression (email, password, confirm, password validity, consent,
  // code validity) moved into collectEmailSignupIssues and now runs on click.
  //
  // KEEP THE IN-FLIGHT DISABLE. It is not a validation gate, it is a double-submit guard: two
  // taps means two register calls, and every one that lands sends a Cognito verification email
  // against a pool capped at 50 a day. The button already renders "Creating account..." here,
  // so a parent is never left wondering why it will not respond.
  const isSubmitDisabled = isLoading;
  // Server errors only, now that client-side validation goes to the pop-up.
  const displayError = error?.message;

  // Always-clickable buttons (2026-08-04): click-time gate for a Google door. Returns false
  // (and opens the dialog) when something is missing, which stops ContinueWithGoogleButton
  // before it captures consent or redirects.
  //
  // The field ids are a parameter because this same validator serves TWO Google buttons in
  // different cards: the Google card's own, and the EMAIL_EXISTS reverse-flow CTA that lives
  // inside the EMAIL card. Focus has to land on the card the parent is actually looking at.
  const validateGoogleDoor = (referralFieldId: string, tncFieldId: string) => (): boolean => {
    const issues = collectGoogleSignupIssues({
      codeInvalid,
      // The reverse-flow CTA reads the email card's tick, the Google card reads its own —
      // exactly as each already passes to consentAccepted. See the reverse-flow comment below
      // for why those two are deliberately different.
      tncChecked: tncFieldId === 'tnc' ? tncChecked : googleTncChecked,
      referralFieldId,
      tncFieldId,
    });
    if (issues.length > 0) {
      setValidationIssues(issues);
      return false;
    }
    return true;
  };

  // Side-by-side cards (2026-07-28): the page only widens into two columns when
  // there are actually two doors. With OAuth unconfigured the Google card does not
  // render, and a md:grid-cols-2 would strand the lone email card in a half-width
  // left column beneath a 4xl-wide heading — so that case keeps the page's original
  // single-column max-w-md geometry unchanged.
  const containerWidthClass = isOAuthConfigured ? 'max-w-md md:max-w-4xl' : 'max-w-md';
  const cardGridColumnsClass = isOAuthConfigured ? 'md:grid-cols-2' : '';

  return (
    <div
      className="relative flex flex-col min-h-screen items-center justify-center px-3 md:px-4 py-8 md:py-12"
      style={{ backgroundColor: '#fdf8f0' }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBg})` }}
        aria-hidden="true"
      />

      {/* Overlay wash for readability */}
      <div className="absolute inset-0 bg-white/60" aria-hidden="true" />

      {/* Content layer */}
      {/* Side-by-side cards (2026-07-28): the container widens past max-w-md on md+
          so the Google and email cards can sit in two columns. It stays max-w-md on
          mobile, where the grid below collapses back to one column. */}
      <div className={`relative z-10 flex flex-col items-center w-full ${containerWidthClass}`}>
        {/* ThinkOtter Logo. max-w caps it at its pre-widening size — 80% of a
            max-w-4xl container would otherwise blow the logo up on desktop. */}
        <div className="mb-6 flex flex-col items-center w-full">
          <img
            src={logoImage}
            alt="ThinkOtter"
            className="w-[80%] max-w-[360px] h-auto"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = document.createElement('span');
              fallback.textContent = 'ThinkOtter';
              fallback.className = 'text-2xl font-bold text-otter-earth-800';
              target.parentElement?.appendChild(fallback);
            }}
          />
        </div>

        {/* Per-method signup cards (2026-07-16): the page now owns the heading, because
            two cards cannot each be titled "Create Account". Classes are unchanged from
            the CardTitle/CardDescription they replace. */}
        <div className="w-full mb-4 space-y-1">
          <h1 className="text-2xl font-bold text-otter-earth-800">Create Account</h1>
          <p className="text-otter-earth-700">Sign up for ThinkOtter</p>
          {/* Side-by-side cards (2026-07-28): with the two doors now sitting next to
              each other rather than one under the other, say out loud that they are
              alternatives — the "or" divider that carried that meaning is mobile-only.
              Conditional on isOAuthConfigured for the same reason the Google card is:
              with OAuth off there is only the email door, and offering a choice that
              is not on screen would be a lie. */}
          {isOAuthConfigured && (
            <p className="text-sm text-otter-earth-700">
              You can sign up with either your Google account or your email address — whichever you
              prefer.
            </p>
          )}
        </div>

        {/* BUG-20260713-020205 (D2, Option B) / T-10: bounce message shown when
            OAuthCallbackPage redirects a not-yet-registered Google account here
            from the login page. Mirrors LoginPage's successMessage banner markup.
            Page-level (2026-07-16): it tells a bounced GOOGLE user to register, so it
            belongs above both doors, not inside the email card. */}
        {bounceMessage && (
          <div className="w-full mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-800 border border-blue-200">
            {bounceMessage}
          </div>
        )}

        {/* Side-by-side cards (2026-07-28): the two doors sit in two columns on md+
            and collapse back to one column (Google first) on mobile. items-start, not
            the default stretch — the Google card is a fraction of the email card's
            height and stretching it to match would render a mostly-empty card.

            COLUMN ORDER IS md:order-*, NOT DOM ORDER (2026-07-28). Desktop reads
            email-left / Google-right, but the Google card is still FIRST in the DOM
            so the mobile stack keeps it on top. Read the two cards' order classes
            together — neither makes sense alone. Consequence, accepted knowingly:
            on md+ the tab and screen-reader order visits the right column before the
            left. It is tolerable only because each card is wholly self-contained
            (own referral field, own consent tick, own button), so either sequence is
            coherent; do NOT extend this trick to content where the order carries
            meaning. Swapping the DOM instead would move that mismatch to mobile AND
            bury the two-field Google path under the long email form on a phone. */}
        <div
          className={`grid w-full grid-cols-1 gap-4 md:gap-6 items-start ${cardGridColumnsClass}`}
        >
          {/* ── Google card ─────────────────────────────────────────────────────
              First in DOM order, because on mobile the cards stack and the email
              form is long enough to push this two-field fast path off the screen
              otherwise. On md+ it is pulled to the RIGHT column by md:order-2. The
              whole block is skipped when the OAuth env is not configured (US-001 T1),
              so this never blocks password registration — note this is also the
              DEFAULT in every test that does not mock isOAuthConfigured, since the
              VITE_COGNITO_* vars are absent outside a real build — which is exactly
              the case the two width classes above collapse back to one column for. */}
          {isOAuthConfigured && (
            <>
              <Card
                data-testid="google-signup-card"
                className="w-full md:order-2 bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]"
              >
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-otter-earth-800">
                    Sign up with Google
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Same binding as the email card's field — the value mirrors. */}
                  <ReferralCodeField
                    id="googleReferralCode"
                    field={referral}
                    disabled={isLoading}
                  />
                  <ConsentCheckbox
                    id="googleTnc"
                    checked={googleTncChecked}
                    onCheckedChange={setGoogleTncChecked}
                    disabled={isLoading}
                  />
                </CardContent>
                <CardFooter>
                  {/* google-sign-in/US-005 (T4) / AC-005.1: pass the typed/URL-prefilled
                    referralCode so it is captured into sessionStorage before redirect.
                    BUG-20260713-020205 (D2, Option B) / T-7: intent='signup' — this
                    is the primary registration door and must never bounce.
                    consentAccepted is THIS card's tick (2026-07-16) — deliberately not
                    the email card's. */}
                  <ContinueWithGoogleButton
                    referralCode={referral.trimmedCode}
                    intent="signup"
                    requireConsent
                    consentAccepted={googleTncChecked}
                    // promo-code US-005 (T3) / AC-005.1 / FR-2.7: gated by the SAME
                    // codeInvalid state as the native button — one code, one verdict.
                    codeBlocked={codeInvalid}
                    codeBlockedNotice={CODE_INVALID_MSG}
                    // Always-clickable buttons (2026-08-04): keeps the button live and moves
                    // both gates above to click time. requireConsent/consentAccepted stay
                    // because they still drive the sessionStorage consent capture; codeBlocked
                    // stays so removing onValidate restores the old behaviour cleanly.
                    onValidate={validateGoogleDoor('googleReferralCode', 'googleTnc')}
                  />
                </CardFooter>
              </Card>

              {/* Side-by-side cards (2026-07-28): md:hidden. This horizontal rule
                  separates a STACK; between two columns it would read as a divider
                  belonging to neither. The "either/or" it carried is now stated in
                  the sentence under the heading, which is why that sentence is not
                  optional decoration. Margins dropped — the grid gap spaces it. */}
              <div className="flex items-center gap-3 w-full md:hidden" aria-hidden="true">
                <div className="h-px flex-1 bg-[#E7D8C8]" />
                <span className="text-xs text-gray-500">or</span>
                <div className="h-px flex-1 bg-[#E7D8C8]" />
              </div>
            </>
          )}

          {/* ── Email card ─────────────────────────────────────────────────────
              Second in the DOM, but md:order-1 puts it in the LEFT column on md+.
              See the grid comment above for why the swap is CSS-only. */}
          <Card
            data-testid="email-signup-card"
            className="w-full md:order-1 bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]"
          >
            <CardHeader>
              <CardTitle className="text-lg font-bold text-otter-earth-800">
                Sign up with email
              </CardTitle>
            </CardHeader>

            {/* Always-clickable buttons (2026-08-04): noValidate turns OFF the browser's own
                validation UI, not the `required` attributes below (those stay, so assistive
                tech still announces the fields as required). With the submit button now always
                clickable, native validation would fire first on an empty field and show its
                own bubble, pre-empting the pop-up entirely. */}
            <form onSubmit={handleSubmit} noValidate>
              <CardContent className="space-y-4">
                <p className="text-xs text-gray-500">
                  Fields marked <span className="text-red-500">*</span> are required
                </p>
                {/* Email — the first field on the form since the Username field was removed.
                    DELIBERATELY NOT autoFocus (2026-07-29): the page must load without
                    grabbing focus. When Email inherited the autoFocus that Username used to
                    carry, arriving from "Sign up" immediately focused it — which on a phone
                    throws up the keyboard and scrolls the card before the parent has read
                    anything, including which fields are even being asked for. If you are
                    tempted to add it back, that is the behaviour you are re-creating.
                    type="email" is load-bearing beyond the keyboard: mobile does NOT
                    autocapitalise an email input, which is what keeps stored addresses
                    lowercase. Do not change it to type="text". */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-otter-earth-800">
                    Email{' '}
                    <span aria-hidden="true" className="text-red-500">
                      *
                    </span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className={OTTER_INPUT_CLASS}
                  />
                </div>

                {/* Display Name field removed 2026-07-29 — the backend defaults it to
                    "Parent" for email signups, and Google signups take the real name from
                    the IdP. Until the parent-dashboard editor ships, every email-registered
                    parent shows as "Parent"; that is a known, accepted consequence. */}

                {/* Referral Code (optional) — position deliberately unchanged. Same
                  binding as the Google card's field above, so the value mirrors. */}
                <ReferralCodeField id="referralCode" field={referral} disabled={isLoading} />

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-otter-earth-800">
                    Password{' '}
                    <span aria-hidden="true" className="text-red-500">
                      *
                    </span>
                  </Label>
                  <PasswordInput
                    id="password"
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className={OTTER_INPUT_CLASS}
                  />
                  {password && <PasswordRequirements password={password} />}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-otter-earth-800">
                    Confirm Password{' '}
                    <span aria-hidden="true" className="text-red-500">
                      *
                    </span>
                  </Label>
                  <PasswordInput
                    id="confirmPassword"
                    toggleLabel="confirm password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="new-password"
                    className={OTTER_INPUT_CLASS}
                  />
                </div>

                {/* Consent checkbox — US-002 / AC-1 / AC-2 / AC-3 / FR-G-001 / NFR-PDPA-001
                  Required, affirmative, unticked-by-default, covering BOTH the Terms of
                  Service and the Privacy Policy. Sign Up stays disabled until it is ticked
                  (isSubmitDisabled). Gates ONLY this card's button — the Google card has
                  its own (2026-07-16). */}
                <ConsentCheckbox
                  id="tnc"
                  checked={tncChecked}
                  onCheckedChange={setTncChecked}
                  disabled={isLoading}
                />

                {/* Error messages */}
                {/* BUG-20260225-155434: Added EMAIL_EXISTS to amber banner conditional - 2026-02-25 */}
                {/* TS-015 / T3: removed INVALID_INVITE_CODE — backend no longer returns it (FR-1.5);
                  referral-code validity is advisory-only and never surfaces as a submit error. */}
                {error?.code === 'SIGNUP_CAP_REACHED' || error?.code === 'EMAIL_EXISTS' ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center space-y-3">
                    <p className="text-sm font-medium text-amber-800">{error.message}</p>
                    {/* google-sign-in/US-003 T3 (AC-003.4, BR-003.2): reverse-flow redirect —
                      a native sign_up on a Google-first email surfaces EMAIL_EXISTS
                      (register.py:206-231, TO-LL-030); this CTA closes the dead-end by
                      redirecting into the SAME single Google door via the shared US-001
                      Amplify trigger (ContinueWithGoogleButton) instead of offering any
                      password-attach flow (out of scope for v1). Shown ONLY for
                      EMAIL_EXISTS, never for SIGNUP_CAP_REACHED. */}
                    {/* google-sign-in/US-005 (T4) / AC-005.1: forward the typed/URL-prefilled
                      referralCode so a referral survives this reverse-flow Google redirect too. */}
                    {/* BUG-20260713-020205 (D2, Option B) / T-7: intent='signup' —
                      this register-page usage never bounces on the callback. */}
                    {/* 2026-07-16: consentAccepted is the EMAIL card's tick, NOT the Google
                      card's — this looks like a bug beside the Google card above, which
                      passes googleTncChecked to the same component, so read on. EMAIL_EXISTS
                      can only come back from a native submit, and isSubmitDisabled includes
                      !tncChecked, so reaching this banner PROVES the email box is ticked and
                      this CTA is always live when shown. Wiring it to googleTncChecked
                      (almost certainly unticked) would disable it and render "accept the
                      Terms above to continue with Google" pointing at a different card,
                      telling the parent to redo what they just did — the exact dead-end
                      US-003 (AC-003.4) exists to close. */}
                    {/* Signup simplification (2026-07-29): EMAIL_EXISTS now has TWO causes,
                        and they need different CTAs.
                          - 'google'   → the address belongs to a Google-first account. Send
                                         them through the Google door (the US-003 flow above).
                          - 'password' → a confirmed native account. Offering Google here
                                         would be a DEAD END: they have a password and no
                                         Google identity, so the button either fails or logs
                                         them into something else. They need "go sign in".
                        Before this, EMAIL_EXISTS could only mean the Google case; the
                        register-side duplicate check made the password case reachable too.
                        `existingProvider` arrives nested in `details` because lib/api.ts
                        builds AuthError from code/message/details ONLY — a top-level field
                        would be silently dropped and this branch would never fire.
                        An ABSENT value deliberately falls to the sign-in CTA: that path is
                        safe for both causes (a Google-first parent can still use Continue
                        with Google from the login page), whereas defaulting the other way
                        would show the Google button to password accounts — the dead end
                        this branch exists to prevent. */}
                    {error?.code === 'EMAIL_EXISTS' &&
                      (error.details?.existingProvider === 'google' ? (
                        isOAuthConfigured && (
                          <ContinueWithGoogleButton
                            referralCode={referral.trimmedCode}
                            intent="signup"
                            requireConsent
                            consentAccepted={tncChecked}
                            // promo-code US-005 (T3) / AC-005.1 / FR-2.7: gate this
                            // reverse-flow Google door from the SAME codeInvalid state.
                            codeBlocked={codeInvalid}
                            codeBlockedNotice={CODE_INVALID_MSG}
                            // Always-clickable buttons (2026-08-04): the EMAIL card's ids,
                            // matching consentAccepted above — this CTA is rendered inside the
                            // email card, so a code problem must focus the field the parent can
                            // see. Consent is provably already ticked here (reaching this
                            // banner required a clean submit), so in practice only the code
                            // rule can fire; wiring it anyway keeps the recovery path from
                            // silently dead-ending if that ever stops being true.
                            onValidate={validateGoogleDoor('referralCode', 'tnc')}
                          />
                        )
                      ) : (
                        <div className="space-y-2">
                          <Link
                            to={ROUTES.LOGIN}
                            className="inline-block w-full rounded-[10px] bg-otter-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-otter-orange-600"
                          >
                            Sign in instead
                          </Link>
                          <p className="text-xs text-amber-800">
                            Forgot your password?{' '}
                            <Link to={ROUTES.FORGOT_PASSWORD} className="underline">
                              Reset it here
                            </Link>
                          </p>
                        </div>
                      ))}
                  </div>
                ) : (
                  displayError && <p className="text-sm text-red-500">{displayError}</p>
                )}
              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  variant="warm"
                  className="w-full rounded-[10px]"
                  disabled={isSubmitDisabled}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Sign Up'
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>

        {/* Page-level (2026-07-16): applies to both doors, so it sits below both cards
            rather than inside the email one. */}
        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>

      {/* Always-clickable buttons (2026-08-04): ONE dialog serves both doors, because only one
          button can be clicked at a time and the issue list already carries which fields it
          means. Rendered at page level (outside the card grid) so it overlays the whole page
          rather than being clipped by a card. */}
      <ValidationAlertDialog
        isVisible={validationIssues !== null}
        issues={validationIssues?.map((issue) => issue.message) ?? []}
        onDismiss={dismissValidationDialog}
      />
    </div>
  );
}
